import { z } from 'zod';

export const isUnknownRecord = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getNonEmptyString = ({
  value,
}: {
  value: unknown;
}): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const emptyStringToUndefined = (value: unknown): unknown =>
  value === '' ? undefined : value;

const WizOptionalStringSchema = z.preprocess(
  emptyStringToUndefined,
  z.string().nullish(),
);

const WizOptionalTimestampSchema = z.preprocess(
  emptyStringToUndefined,
  z.iso.datetime().nullish().catch(undefined),
);

export const WIZ_WEBHOOK_TRIGGER_SOURCES = [
  'ISSUE',
  'DETECTION',
  'THREAT',
] as const;

export const WizTriggerSourceSchema = z.enum(WIZ_WEBHOOK_TRIGGER_SOURCES);

export type WizWebhookTriggerSource = z.infer<typeof WizTriggerSourceSchema>;

const WIZ_TRIGGER_SOURCE_BY_ALIAS: Record<string, WizWebhookTriggerSource> = {
  ISSUE: 'ISSUE',
  ISSUES: 'ISSUE',
  DETECTION: 'DETECTION',
  DETECTIONS: 'DETECTION',
  THREAT: 'THREAT',
  THREATS: 'THREAT',
};

const canonicalizeWizTriggerSource = (value: unknown): unknown =>
  typeof value === 'string'
    ? (WIZ_TRIGGER_SOURCE_BY_ALIAS[value.trim().toUpperCase()] ?? value)
    : value;

export const WizTriggerTypeSchema = z.enum([
  'Created',
  'Updated',
  'Opened',
  'Closed',
  'Reopened',
]);

export const WizIssueStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'RESOLVED',
  'REJECTED',
]);

export const WizSeveritySchema = z.enum([
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'INFORMATIONAL',
]);

const WizReportedSeveritySchema = z.preprocess(
  emptyStringToUndefined,
  WizSeveritySchema.default('INFORMATIONAL'),
);

const WizOptionalSeveritySchema = z.preprocess(
  emptyStringToUndefined,
  WizSeveritySchema.nullish(),
);

const WizOptionalIssueStatusSchema = z.preprocess(
  emptyStringToUndefined,
  WizIssueStatusSchema.nullish().catch(undefined),
);

const WizWebhookTriggerBaseSchema = z.looseObject({
  type: WizTriggerTypeSchema.nullish().catch(undefined),
  ruleId: z.string().min(1),
  ruleName: WizOptionalStringSchema,
  updatedFields: WizOptionalStringSchema,
  changedBy: WizOptionalStringSchema,
});

const WizEntityReferenceSchema = z.looseObject({
  id: WizOptionalStringSchema,
  name: WizOptionalStringSchema,
  type: WizOptionalStringSchema,
  cloudPlatform: WizOptionalStringSchema,
});

const WizIssueSchema = z.looseObject({
  id: z.string().min(1),
  status: WizOptionalIssueStatusSchema,
  severity: WizReportedSeveritySchema,
  created: WizOptionalTimestampSchema,
  projects: z.unknown().optional(),
  evidence: z.unknown().optional(),
});

const WizIssueResourceSchema = z.looseObject({
  id: WizOptionalStringSchema,
  name: WizOptionalStringSchema,
  type: WizOptionalStringSchema,
  cloudPlatform: WizOptionalStringSchema,
  subscriptionId: WizOptionalStringSchema,
  subscriptionName: WizOptionalStringSchema,
  region: WizOptionalStringSchema,
  status: WizOptionalStringSchema,
  cloudProviderURL: WizOptionalStringSchema,
});

const WizIssueControlSchema = z.looseObject({
  id: WizOptionalStringSchema,
  name: WizOptionalStringSchema,
  description: WizOptionalStringSchema,
  severity: WizOptionalSeveritySchema,
  risks: z.array(z.string()).optional(),
  resolutionRecommendation: WizOptionalStringSchema,
  sourceRuleId: WizOptionalStringSchema,
  sourceRuleName: WizOptionalStringSchema,
  sourceCloudConfigurationRule: z
    .looseObject({
      id: WizOptionalStringSchema,
      name: WizOptionalStringSchema,
    })
    .optional(),
});

const WizDetectionTriggerSchema = WizWebhookTriggerBaseSchema.extend({
  source: z.preprocess(canonicalizeWizTriggerSource, z.literal('DETECTION')),
});

const WizThreatTriggerSchema = WizWebhookTriggerBaseSchema.extend({
  source: z.preprocess(canonicalizeWizTriggerSource, z.literal('THREAT')),
});

const WizIssueTriggerSchema = WizWebhookTriggerBaseSchema.extend({
  source: z.preprocess(canonicalizeWizTriggerSource, z.literal('ISSUE')),
});

const WizDetectionRuntimeDetailsSchema = z.looseObject({
  processTree: z.array(z.unknown()).optional(),
});

const WizDetectionTriggeringEventSchema = z.looseObject({
  actor: z.unknown().optional(),
  actorIP: WizOptionalStringSchema,
  actorIPMeta: z.unknown().optional(),
  category: WizOptionalStringSchema,
  description: WizOptionalStringSchema,
  runtimeDetails: WizDetectionRuntimeDetailsSchema.optional(),
});

export const WizIssueWebhookPayloadSchema = z.looseObject({
  trigger: WizIssueTriggerSchema,
  issue: WizIssueSchema,
  resource: WizIssueResourceSchema,
  control: WizIssueControlSchema,
});

export const WizDetectionWebhookPayloadSchema = z.looseObject({
  trigger: WizDetectionTriggerSchema,
  id: z.string().min(1),
  threatId: WizOptionalStringSchema,
  threatURL: WizOptionalStringSchema,
  title: WizOptionalStringSchema,
  description: WizOptionalStringSchema,
  severity: WizReportedSeveritySchema,
  createdAt: WizOptionalTimestampSchema,
  tdrId: WizOptionalStringSchema,
  tdrSource: WizOptionalStringSchema,
  mitreTactics: z.array(z.string()).optional(),
  mitreTechniques: z.array(z.string()).optional(),
  cloudAccounts: z.array(z.unknown()).optional(),
  timeframe: z.unknown().optional(),
  actors: z.array(z.unknown()).optional(),
  primaryResource: WizEntityReferenceSchema.optional(),
  resources: z.array(WizEntityReferenceSchema).optional(),
  triggeringEventsCount: z.int().nonnegative().optional().catch(undefined),
  triggeringEvents: z.array(WizDetectionTriggeringEventSchema).optional(),
});

const WizThreatSchema = z.looseObject({
  id: z.string().min(1),
  title: WizOptionalStringSchema,
  description: WizOptionalStringSchema,
  status: WizOptionalStringSchema,
  severity: WizReportedSeveritySchema,
  created: WizOptionalTimestampSchema,
  projects: z.unknown().optional(),
  threatURL: WizOptionalStringSchema,
  cloudPlatform: WizOptionalStringSchema,
  cloudAccounts: z.array(z.unknown()).optional(),
  actors: z.array(z.unknown()).optional(),
  resources: z.array(WizEntityReferenceSchema).optional(),
  tdrNames: z.array(z.string()).optional(),
  detectionIds: z.array(z.string()).optional(),
  mitreTechniques: z.array(z.string()).optional(),
  mitreTactics: z.array(z.string()).optional(),
  notes: z.unknown().optional(),
});

export const WizThreatWebhookPayloadSchema = z.looseObject({
  trigger: WizThreatTriggerSchema,
  threat: WizThreatSchema,
});

export type WizIssueWebhookPayload = z.infer<
  typeof WizIssueWebhookPayloadSchema
>;
export type WizDetectionWebhookPayload = z.infer<
  typeof WizDetectionWebhookPayloadSchema
>;
export type WizThreatWebhookPayload = z.infer<
  typeof WizThreatWebhookPayloadSchema
>;
export type WizWebhookPayload =
  | WizIssueWebhookPayload
  | WizDetectionWebhookPayload
  | WizThreatWebhookPayload;

const WizWebhookEnvelopeSchema = z.looseObject({
  trigger: WizWebhookTriggerBaseSchema.extend({
    source: z.preprocess(canonicalizeWizTriggerSource, WizTriggerSourceSchema),
  }),
});

const WIZ_PAYLOAD_SCHEMA_BY_TRIGGER_SOURCE = {
  ISSUE: WizIssueWebhookPayloadSchema,
  DETECTION: WizDetectionWebhookPayloadSchema,
  THREAT: WizThreatWebhookPayloadSchema,
} as const;

export type WizWebhookParseResult =
  | { success: true; data: WizWebhookPayload }
  | { success: false; issues: z.core.$ZodIssue[] };

export const parseWizWebhookPayload = ({
  body,
}: {
  body: unknown;
}): WizWebhookParseResult => {
  const parsedEnvelope = WizWebhookEnvelopeSchema.safeParse(body);

  if (!parsedEnvelope.success) {
    return { success: false, issues: parsedEnvelope.error.issues };
  }

  const parsedBranch = WIZ_PAYLOAD_SCHEMA_BY_TRIGGER_SOURCE[
    parsedEnvelope.data.trigger.source
  ].safeParse(parsedEnvelope.data);

  return parsedBranch.success
    ? { success: true, data: parsedBranch.data }
    : { success: false, issues: parsedBranch.error.issues };
};

export const isWizIssueWebhookPayload = (
  payload: WizWebhookPayload,
): payload is WizIssueWebhookPayload => payload.trigger.source === 'ISSUE';

export const isWizDetectionWebhookPayload = (
  payload: WizWebhookPayload,
): payload is WizDetectionWebhookPayload =>
  payload.trigger.source === 'DETECTION';

export const extractWizPayloadId = ({
  payload,
}: {
  payload: WizWebhookPayload;
}): string => {
  if (isWizIssueWebhookPayload(payload)) {
    return payload.issue.id;
  }

  if (isWizDetectionWebhookPayload(payload)) {
    return payload.id;
  }

  return payload.threat.id;
};

export type WizEmptyIdentifyingTemplateSkip = {
  ruleId: string | null;
  ruleName: string | null;
  triggerSource: string | null;
  emptyFields: string[];
};

const readWizIdentifyingField = ({
  payload,
  source,
}: {
  payload: Record<string, unknown>;
  source: WizWebhookTriggerSource;
}): { path: string; value: unknown } => {
  if (source === 'ISSUE') {
    return {
      path: 'issue.id',
      value: isUnknownRecord(payload.issue) ? payload.issue.id : undefined,
    };
  }

  if (source === 'DETECTION') {
    return { path: 'id', value: payload.id };
  }

  return {
    path: 'threat.id',
    value: isUnknownRecord(payload.threat) ? payload.threat.id : undefined,
  };
};

export const describeWizEmptyIdentifyingTemplateSkip = ({
  payload,
}: {
  payload: unknown;
}): WizEmptyIdentifyingTemplateSkip | null => {
  if (!isUnknownRecord(payload) || !isUnknownRecord(payload.trigger)) {
    return null;
  }

  const { trigger } = payload;
  const canonicalSource = canonicalizeWizTriggerSource(trigger.source);
  if (
    canonicalSource !== 'ISSUE' &&
    canonicalSource !== 'DETECTION' &&
    canonicalSource !== 'THREAT'
  ) {
    return null;
  }

  const identifyingField = readWizIdentifyingField({
    payload,
    source: canonicalSource,
  });
  if (identifyingField.value !== '') {
    return null;
  }

  return {
    ruleId: getNonEmptyString({ value: trigger.ruleId }),
    ruleName: getNonEmptyString({ value: trigger.ruleName }),
    triggerSource: getNonEmptyString({ value: trigger.source }),
    emptyFields: [identifyingField.path],
  };
};
